import { LogOut, User, Menu, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ThemeToggle } from "@/components/ThemeToggle";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { AdminModeToggle } from "./AdminModeToggle";
import { HeaderSubscriptionCTA } from "@/components/subscription/HeaderSubscriptionCTA";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useNavigate, Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import vlePlusLogo from "@/assets/logo.jpeg";

export function AppHeader() {
  const { user, profile, roles, signOut, isTenantOwner, isPlatformAdmin } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await signOut();
    navigate('/auth');
  };

  const getInitials = (name: string | null | undefined) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleBadge = () => {
    if (isPlatformAdmin) return { label: 'Platform Admin', variant: 'destructive' as const };
    if (isTenantOwner) return { label: t('business_owner') || 'Business Owner', variant: 'default' as const };
    return { label: 'User', variant: 'outline' as const };
  };

  const roleBadge = getRoleBadge();

  return (
    <header className="sticky top-0 z-50 flex h-14 items-center gap-4 border-b border-border bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <SidebarTrigger className="-ml-1">
        <Menu className="h-5 w-5" />
      </SidebarTrigger>

      {/* Logo */}
      <Link to="/dashboard" className="flex items-center gap-2">
        <img src={vlePlusLogo} alt="VLE Plus Logo" className="w-7 h-7 rounded object-contain" />
        <span className="font-semibold text-foreground hidden sm:inline">VLE Plus</span>
      </Link>

      {/* Platform Admin Link - Only for platform admins */}
      {isPlatformAdmin && (
        <Button 
          variant="outline" 
          size="sm" 
          className="gap-2 border-destructive/50 text-destructive hover:bg-destructive/10"
          onClick={() => navigate('/platform/dashboard')}
        >
          <Shield className="h-4 w-4" />
          <span className="hidden sm:inline">Platform</span>
        </Button>
      )}

      {/* Admin Mode Toggle - For business owners */}
      {isTenantOwner && <AdminModeToggle />}

      <div className="flex-1" />

      <div className="flex items-center gap-2">
        {/* Subscription CTA - Always visible */}
        <HeaderSubscriptionCTA />
        
        <LanguageSwitcher />
        <ThemeToggle />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-9 gap-2 pl-2 pr-3">
              <Avatar className="h-7 w-7">
                <AvatarImage src={profile?.avatar_url || undefined} />
                <AvatarFallback className="text-xs">
                  {getInitials(profile?.full_name)}
                </AvatarFallback>
              </Avatar>
              <div className="hidden flex-col items-start md:flex">
                <span className="text-sm font-medium">
                  {profile?.full_name || user?.email?.split('@')[0]}
                </span>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">
                  {profile?.full_name || 'User'}
                </p>
                <p className="text-xs leading-none text-muted-foreground">
                  {user?.email}
                </p>
                <Badge variant={roleBadge.variant} className="mt-1 w-fit">
                  {roleBadge.label}
                </Badge>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings')}>
              <User className="mr-2 h-4 w-4" />
              Profile
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              {t('logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
